 import apiClient from './apiClient';
 import { io } from 'socket.io-client';
 import { getBackendUrl } from '../utils/apiUrl';

 // Use the same base URL pattern as other socket services
 const baseUrl = getBackendUrl();

class NotificationService {
  /**
   * Get all notifications for the current user
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 20)
   * @param {string} params.type - Filter by notification type
   * @param {boolean} params.unreadOnly - Only return unread notifications
   * @returns {Promise<{notifications: Array, total: number, hasMore: boolean}>}
   */
  async getNotifications(params = {}) {
    const queryParams = new URLSearchParams({
      page: params.page || 1,
      limit: params.limit || 20,
      ...(params.type && { type: params.type }),
      ...(params.unreadOnly && { unreadOnly: 'true' })
    });

    const response = await apiClient.get(`/notifications?${queryParams}`);
    return response;
  }

  /**
   * Get notification counts by type
   * @returns {Promise<NotificationCounts>}
   */
  async getNotificationCounts() {
    const response = await apiClient.get('/notifications/counts');
    return response;
  }

  /**
   * Mark a notification as read
   * @param {string} notificationId - The notification ID
   * @returns {Promise<void>}
   */
  async markAsRead(notificationId) {
    await apiClient.patch(`/notifications/${notificationId}/read`);
  }

  /**
   * Mark multiple notifications as read
   * @param {string[]} notificationIds - Array of notification IDs
   * @returns {Promise<void>}
   */
  async markMultipleAsRead(notificationIds) {
    await apiClient.patch('/notifications/mark-read', { notificationIds });
  }

  /**
   * Mark all notifications as read
   * @returns {Promise<void>}
   */
  async markAllAsRead() {
    await apiClient.patch('/notifications/mark-all-read');
  }

  /**
   * Delete a notification
   * @param {string} notificationId - The notification ID
   * @returns {Promise<void>}
   */
  async deleteNotification(notificationId) {
    await apiClient.delete(`/notifications/${notificationId}`);
  }

  /**
   * Get notification preferences
   * @returns {Promise<NotificationPreferences>}
   */
  async getPreferences() {
    const response = await apiClient.get('/notifications/preferences');
    return response;
  }

  /**
   * Update notification preferences
   * @param {NotificationPreferences} preferences - Updated preferences
   * @returns {Promise<NotificationPreferences>}
   */
  async updatePreferences(preferences) {
    const response = await apiClient.put('/notifications/preferences', preferences);
    return response;
  }

  /**
   * Subscribe to real-time notifications via Socket.IO
   * @param {Function} onNotification - Callback for new notifications
   * @param {Function} onError - Error callback
   * @returns {{close: Function}} Connection controller with a close() method
   */
  subscribeToNotifications(onNotification, onError, onNursysUpdate) {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token') || localStorage.getItem('accessToken');
    const socket = io(`${baseUrl}/ws/notifications`, {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
      forceNew: true,
      timeout: 15000,
    });

    socket.on('connect', () => {
      console.log('Notifications socket connected:', socket.id);
    });

    socket.on('notification', (payload) => {
      try {
        onNotification(payload);
      } catch (err) {
        console.error('Error handling notification payload:', err);
        onError?.(err);
      }
    });

    // Listen for Nursys enrollment status updates
    socket.on('nursys_update', (payload) => {
      try {
        onNursysUpdate?.(payload);
      } catch (err) {
        console.error('Error handling nursys_update payload:', err);
      }
    });

    socket.on('connect_error', (err) => {
      console.error('Notifications socket connect_error:', err);
      onError?.(err);
    });

    socket.on('error', (err) => {
      console.error('Notifications socket error:', err);
      onError?.(err);
    });

    socket.on('disconnect', (reason) => {
      console.log('Notifications socket disconnected:', reason);
    });

    // Return an object with a close() method and the raw socket
    return {
      socket,
      close: () => {
        try {
          socket.disconnect();
        } catch (e) {
          console.warn('Error disconnecting notifications socket:', e);
        }
      }
    };
  }
}

const notificationService = new NotificationService();
export default notificationService;
